import { useState, FormEvent } from "react";

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConsultationModal = ({ isOpen, onClose }: ConsultationModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    industry: "",
    message: "",
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !consent) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams();
      body.append("name", formData.name);
      body.append("phone", formData.phone);
      body.append("email", formData.email);
      body.append("industry", formData.industry);
      body.append("message", formData.message);
      const res = await fetch("https://readdy.ai/api/form/d7066att4s7j0tv5inkg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const handleClose = () => {
    setStatus("idle");
    setConsent(false);
    setFormData({ name: "", phone: "", email: "", industry: "", message: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/50"></div>
      <div
        className="relative bg-white rounded-xl w-full max-w-lg p-7 lg:p-8 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {status === "success" ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-green-500"></i>
              </div>
            </div>
            <div>
              <p className="font-bold text-[#1A1A2E] text-xl mb-2">Dziękuję!</p>
              <p className="text-gray-500 text-sm">
                Odezwę się do Ciebie w ciągu 24 godzin. Jeśli chcesz szybciej — zadzwoń:{" "}
                <a href="tel:+48509963499" className="text-[#0F2557] font-semibold">
                  +48 509 963 499
                </a>
              </p>
            </div>
            <button
              onClick={handleClose}
              className="whitespace-nowrap mt-4 px-6 py-2.5 rounded-md font-semibold text-sm bg-[#0F2557] text-white hover:bg-[#1B3D7A] transition-colors cursor-pointer"
            >
              Zamknij
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#1A1A2E] mb-1">
                Bezpłatna konsultacja
              </h3>
              <p className="text-sm text-gray-500">
                Zostaw dane — odezwę się i omówimy Twoją stronę. Bez zobowiązań.
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Imię i nazwa firmy <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Jan Kowalski / Firma Kowalski"
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#0F2557] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Telefon <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+48 500 000 000"
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#0F2557] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="jan@firma.pl"
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#0F2557] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Branża / rodzaj działalności
                </label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={(e) =>
                    setFormData({ ...formData, industry: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#0F2557] transition-colors cursor-pointer"
                >
                  <option value="">Wybierz branżę...</option>
                  <option>Elektryk</option>
                  <option>Hydraulik</option>
                  <option>Dekarz</option>
                  <option>Brukarz</option>
                  <option>Ogrodzenia</option>
                  <option>Carporty</option>
                  <option>Klimatyzacja</option>
                  <option>Remonty</option>
                  <option>Ogrodnictwo</option>
                  <option>Malowanie</option>
                  <option>Inna usługa lokalna</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Coś do dodania? (opcjonalnie)
                </label>
                <textarea
                  name="message"
                  rows={2}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="Np. działam w Krakowie, potrzebuję szybko..."
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#0F2557] transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {formData.message.length}/500 znaków
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#0F2557] cursor-pointer"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  Wyrażam zgodę na przetwarzanie moich danych osobowych przez Administratora w celu odpowiedzi na zapytanie, zgodnie z{" "}
                  <a href="/polityka-prywatnosci" target="_blank" className="text-[#0F2557] underline hover:no-underline">Polityką Prywatności</a>.
                  Zgoda jest dobrowolna i można ją wycofać w dowolnym momencie. <span className="text-red-500">*</span>
                </span>
              </label>

              {status === "error" && (
                <p className="text-red-500 text-sm">
                  Wystąpił błąd. Spróbuj ponownie lub zadzwoń na{" "}
                  <a href="tel:+48509963499" className="font-semibold">
                    +48 509 963 499
                  </a>
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending" || !consent}
                className="whitespace-nowrap w-full py-3 rounded-md font-semibold text-sm bg-[#0F2557] text-white hover:bg-[#1B3D7A] transition-colors cursor-pointer disabled:opacity-60 mt-1"
              >
                {status === "sending" ? "Wysyłanie..." : "Umów bezpłatną konsultację"}
              </button>

              <p className="text-xs text-gray-400 text-center">
                Odezwę się w ciągu 24h. Bez spamu i bez zobowiązań.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;
